/**
 * ProvenanceViewer — behavioral port of the @smartbox/ui ProvenanceViewer web component.
 *
 * READ-ONLY. Given a factId, fetches the ProvenanceChain via
 * GET /api/sb/facts/:factId/provenance and renders the event timeline.
 *
 * ORDERING: Events are rendered newest-first (descending by timestamp).
 * Rationale: in a field context the most recent event (correction, commit)
 * is the most immediately relevant. The raw order from the server is
 * ascending; we reverse it on render.
 *
 * The factId is an opaque UUID — no PII is derived from it for display.
 * The detail field is rendered as a JSON key-value list since its shape
 * is category-dependent and unknown at compile time.
 *
 * NEVER imports @smartbox/* (mobile CLAUDE.md invariant 1).
 */

import { useEffect, useState } from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  ActivityIndicator,
  Pressable,
} from 'react-native';
import type { ProvenanceChain, ProvenanceEvent } from '@beam/api-types';
import { provenanceChain } from '../../api/smartbox';
import { ApiRequestError } from '../../api/client';
import { Text, Card, Button } from '../ui';
import { theme } from '../../theme';

interface Props {
  factId: string;
}

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'ready'; chain: ProvenanceChain };

// Map each provenance event type to a semantic status colour pair from the design system.
// Copper is never a status, so we lean on the status palette + palette washes.
const EVENT_DOT_COLOR: Record<string, string> = {
  extracted:  theme.status.sent.fg,      // blueprint blue
  ingested:   theme.color.text.secondary, // muted
  committed:  theme.status.paid.fg,      // green
  corrected:  theme.status.inProgress.fg, // amber
  superseded: theme.status.overdue.fg,   // red
};

const EVENT_BADGE_BG: Record<string, string> = {
  extracted:  theme.status.sent.bg,
  ingested:   theme.color.bg.subtle,
  committed:  theme.status.paid.bg,
  corrected:  theme.status.inProgress.bg,
  superseded: theme.status.overdue.bg,
};

function eventDotColor(eventType: string): string {
  return EVENT_DOT_COLOR[eventType] ?? theme.color.text.secondary;
}
function eventBadgeBg(eventType: string): string {
  return EVENT_BADGE_BG[eventType] ?? theme.color.bg.subtle;
}

function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function DetailList({ detail }: { detail: Record<string, unknown> }) {
  const entries = Object.entries(detail).filter(([, v]) => v !== undefined && v !== null);
  if (entries.length === 0) return null;
  return (
    <View style={styles.detailList}>
      {entries.map(([k, v]) => (
        <View key={k} style={styles.detailRow}>
          <Text variant="label" color="secondary" style={styles.detailKey}>{k}</Text>
          <Text variant="data" color="primary" style={styles.detailValue} numberOfLines={3}>
            {typeof v === 'string' ? v : JSON.stringify(v)}
          </Text>
        </View>
      ))}
    </View>
  );
}

function EventCard({ event, index, total }: { event: ProvenanceEvent; index: number; total: number }) {
  const [expanded, setExpanded] = useState(index === 0); // newest expanded by default
  const dotColor = eventDotColor(event.eventType);
  const badgeBg = eventBadgeBg(event.eventType);
  const hasDetail = Object.keys(event.detail).length > 0;

  return (
    <View style={styles.eventRow} accessibilityRole="text">
      {/* Timeline connector column */}
      <View style={styles.timelineColumn}>
        <View style={[styles.dot, { backgroundColor: dotColor }]} />
        {index < total - 1 && <View style={styles.connector} />}
      </View>

      <Card style={styles.eventCard}>
        {/* Type badge + timestamp */}
        <View style={styles.eventHeader}>
          <View style={[styles.typeBadge, { backgroundColor: badgeBg }]}>
            <Text variant="label" style={{ color: dotColor }}>
              {event.eventType.toUpperCase()}
            </Text>
          </View>
          <Text variant="data" color="secondary" style={styles.timestamp}>
            {formatTimestamp(event.timestamp)}
          </Text>
        </View>

        {/* Actor */}
        <Text variant="bodyStrong" accessibilityLabel={`Actor: ${event.actor}`}>
          {event.actor}
        </Text>

        {/* Detail (collapsible) */}
        {hasDetail && (
          <Pressable
            onPress={() => setExpanded((v) => !v)}
            accessibilityRole="button"
            accessibilityLabel={expanded ? 'Collapse detail' : 'Expand detail'}
          >
            <Text variant="small" color="link">
              {expanded ? 'Hide detail' : 'Show detail'}
            </Text>
          </Pressable>
        )}
        {hasDetail && expanded && <DetailList detail={event.detail} />}
      </Card>
    </View>
  );
}

export function ProvenanceViewer({ factId }: Props) {
  const [state, setState] = useState<LoadState>({ phase: 'loading' });

  function load() {
    setState({ phase: 'loading' });
    provenanceChain(factId)
      .then((chain) => setState({ phase: 'ready', chain }))
      .catch((err) => {
        const msg =
          err instanceof ApiRequestError
            ? err.message
            : err instanceof Error
              ? err.message
              : 'Failed to load provenance chain.';
        setState({ phase: 'error', message: msg });
      });
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [factId]);

  if (state.phase === 'loading') {
    return (
      <View style={styles.centeredState}>
        <ActivityIndicator size="large" color={theme.color.action.primary} accessibilityLabel="Loading provenance" />
        <Text variant="body" color="secondary">Loading provenance…</Text>
      </View>
    );
  }

  if (state.phase === 'error') {
    return (
      <View style={styles.centeredState}>
        <View accessibilityRole="alert">
          <Card style={styles.errorCard}>
            <Text variant="small" color="danger">{state.message}</Text>
          </Card>
        </View>
        <Button
          title="Retry"
          variant="secondary"
          size="md"
          fullWidth={false}
          onPress={load}
          accessibilityLabel="Retry loading provenance"
        />
      </View>
    );
  }

  const { chain } = state;
  // Newest-first: reverse the server's ascending order.
  const events = [...chain.events].reverse();

  return (
    <ScrollView
      style={styles.scrollView}
      contentContainerStyle={styles.scrollContent}
    >
      {/* Fact ID (read-only, opaque) */}
      <Card>
        <Text variant="label" color="secondary">Fact ID</Text>
        <Text variant="data" selectable accessibilityLabel={`Fact ID: ${chain.factId}`}>
          {chain.factId}
        </Text>
      </Card>

      {/* Ordering note / event count */}
      <Text variant="label" color="secondary">
        {events.length} {events.length === 1 ? 'event' : 'events'} — newest first
      </Text>

      {events.length === 0 ? (
        <View style={styles.emptyBox}>
          <Text variant="body" color="secondary" style={styles.centered}>
            No provenance events recorded for this fact.
          </Text>
        </View>
      ) : (
        <View style={styles.timeline} accessibilityRole="list">
          {events.map((event, i) => (
            <EventCard
              key={`${event.eventType}-${event.timestamp}-${i}`}
              event={event}
              index={i}
              total={events.length}
            />
          ))}
        </View>
      )}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  centeredState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: theme.spacing['3xl'],
    gap: theme.spacing.lg,
  },
  errorCard: {
    backgroundColor: theme.status.overdue.bg,
    borderColor: theme.color.action.danger,
    width: '100%',
  },
  scrollView: {
    flex: 1,
    backgroundColor: theme.color.bg.app,
  },
  scrollContent: {
    paddingHorizontal: theme.layout.screenPadding,
    paddingVertical: theme.layout.screenPadding,
    paddingBottom: theme.spacing['4xl'],
    gap: theme.spacing.md,
  },
  emptyBox: {
    paddingVertical: theme.spacing['2xl'],
    alignItems: 'center',
  },
  centered: {
    textAlign: 'center',
  },
  timeline: {
    gap: 0,
  },
  eventRow: {
    flexDirection: 'row',
    gap: theme.spacing.md,
    paddingVertical: theme.spacing.xs,
  },
  timelineColumn: {
    alignItems: 'center',
    width: theme.spacing.xl,
    paddingTop: theme.spacing.xs,
  },
  dot: {
    width: theme.spacing.md,
    height: theme.spacing.md,
    borderRadius: theme.radius.pill,
  },
  connector: {
    flex: 1,
    width: theme.layout.hairline * 2,
    backgroundColor: theme.color.border.default,
    marginTop: theme.spacing.xs,
    minHeight: theme.spacing.xl,
  },
  eventCard: {
    flex: 1,
    marginBottom: theme.spacing.sm,
  },
  eventHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing.sm,
  },
  typeBadge: {
    borderRadius: theme.radius.pill,
    paddingHorizontal: theme.spacing.sm,
    paddingVertical: theme.spacing.xs,
  },
  timestamp: {
    flex: 1,
    textAlign: 'right',
  },
  detailList: {
    backgroundColor: theme.color.bg.subtle,
    borderRadius: theme.radius.sm,
    padding: theme.spacing.sm,
    gap: theme.spacing.xs,
    borderWidth: theme.layout.hairline,
    borderColor: theme.color.border.default,
  },
  detailRow: {
    flexDirection: 'row',
    gap: theme.spacing.sm,
    alignItems: 'flex-start',
  },
  detailKey: {
    minWidth: 72,
  },
  detailValue: {
    flex: 1,
  },
});
